import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import * as Model from '../models';
import { generateId } from '../models/util';
import { AddProjectRequest, BatchOligoParamDefs, BatchOligoParams, ProjectService } from '../service/project-service';
import { PricingResult, PricingService } from '../service/pricing-service';

export const ADD_PROJECT = 'ADD_PROJECT';
export const UPDATE_PROJECT = 'UPDATE_PROJECT';
export const DELETE_PROJECT = 'DELETE_PROJECT';
export const LOAD_PROJECTS = 'LOAD_PROJECTS';

export type ProjectAddActionPayload = Model.ProjectItem;

export type ProjectAddFnRequest = {
  nextIdx: number;
};

export type ProjectAddFn = ((request: ProjectAddFnRequest) => ThunkAction<ng.IPromise<Model.ProjectItem>, Model.RootState, object, Model.ReduxAction<ProjectAddActionPayload>>);

export type ProjectAddDispatch = ThunkDispatch<Model.RootState, object, Model.ReduxAction<ProjectAddActionPayload>>;

export interface ProjectAddAction {
  addProject: ProjectAddFn;
}

export type ProjectAdd = (request: ProjectAddFnRequest) => ng.IPromise<Model.ProjectItem | null>;

export interface ProjectAddSupport {
  addProject: ProjectAdd | null;
}

export type ProjectUpdateActionPayload = Model.ProjectItem;

export type ProjectUpdateFnRequest = Model.ProjectItem;

export type ProjectUpdateReduxAction = Model.ReduxAction<ProjectUpdateActionPayload>;

export type ProjectUpdateFn = ((request: ProjectUpdateFnRequest) => ThunkAction<ng.IPromise<Model.ProjectItem>, Model.RootState, object, ProjectUpdateReduxAction>);

export type ProjectUpdateDispatch = ThunkDispatch<Model.RootState, object, ProjectUpdateReduxAction>;

export interface ProjectUpdateAction {
  updateProject: ProjectUpdateFn;
}

export type ProjectUpdate = (request: ProjectUpdateFnRequest) => ng.IPromise<Model.ProjectItem | null>;

export interface ProjectUpdateSupport {
  updateProject: ProjectUpdate | null;
}

export type ProjectDeleteFnRequest = {
  id: string;
  index: number;
};

export type ProjectDeleteActionPayload = ProjectDeleteFnRequest;

export type ProjectDeleteFn = ((request: ProjectDeleteFnRequest) => ThunkAction<ng.IPromise<Model.ProjectItem>, Model.RootState, object, Model.ReduxAction<ProjectDeleteActionPayload>>);

export type ProjectDeleteDispatch = ThunkDispatch<Model.RootState, object, Model.ReduxAction<ProjectDeleteActionPayload>>;

export interface ProjectDeleteAction {
  deleteProject: ProjectDeleteFn;
}

export type ProjectDelete = (request: ProjectDeleteFnRequest) => ng.IPromise<Model.ProjectItem | null>;

export interface ProjectDeleteSupport {
  deleteProject: ProjectDelete | null;
}

export type ProjectImportActionPayload = Model.ProjectItem[];

export type ProjectImportFnRequest = {
  projects: Model.ProjectItem[];
};

export type ProjectImportFnResponse = {
  projects: Model.ProjectItem[];
};

export type ProjectImportFn = ((request: ProjectImportFnRequest) => ThunkAction<ng.IPromise<ProjectImportFnResponse>, Model.RootState, object, Model.ReduxAction<ProjectImportActionPayload>>);

export type ProjectImportDispatch = ThunkDispatch<Model.RootState, object, Model.ReduxAction<ProjectImportActionPayload>>;

export interface ProjectImportAction {
  importProject: ProjectImportFn;
}

export type ProjectImport = (request: ProjectImportFnRequest) => ng.IPromise<ProjectImportFnResponse>;

export interface ProjectImportSupport {
  importProject: ProjectImport | null;
}

export interface ProjectActions extends ProjectAddAction, ProjectUpdateAction, ProjectDeleteAction, ProjectImportAction {
}

function isPricingCandidate(oligo: Model.OligoItem): boolean {
  const { definition } = oligo;
  const { basic, properties } = definition;
  const { oligoValue } = basic;
  const { pricing: pricingProps } = properties;
  const { supported: pricingSupported } = pricingProps;
  const oligoValueDefined: boolean = oligoValue.value ?  oligoValue.value.length > 0 : false;
  return pricingSupported && oligoValueDefined;
}

function oligosUsingPreference($q: ng.IQService,
                               projectService: ProjectService,
                               pricingService: PricingService): (preference: Model.ProjectPreference, oligoItems: Model.OligoItem[]) => ng.IPromise<Model.OligoItem[]> {
  return (preference: Model.ProjectPreference, oligoItems: Model.OligoItem[]): ng.IPromise<Model.OligoItem[]> => {
    type OligoItemMap = { [key: string]: Model.OligoItem };
    const deferred: ng.IDeferred<Model.OligoItem[]> = $q.defer();
    const { scale, purification, format } = preference;
    const [ parameterMap, oligoItemMap ] = oligoItems.reduce((acc: [BatchOligoParams, OligoItemMap], oligoItem: Model.OligoItem) => {
      const [parameters, oligos] = acc;
      const { id } = oligoItem;
      const parameter: Model.OligoParam = { ...oligoItem.parameter };
      parameter.basic = {
        ...parameter.basic,
        ...{ scale },
        ...{ purification },
        ...{ format },
      };
      parameters[id] = parameter;
      oligos[id] = oligoItem;
      return acc;
    }, [{}, {}] as [BatchOligoParams, OligoItemMap]);
    projectService.oligoParameterDefinitionBatch(parameterMap).then((paramDefsMap: BatchOligoParamDefs) => {
      const [updatedOligoList, updatedOligoMap] = Object.keys(oligoItemMap).reduce((acc: [Model.OligoItem[], OligoItemMap], id: string) => {
        const [itemList, itemMap] = acc;
        const oligoItem: Model.OligoItem = oligoItemMap[id];
        const { index, displayType, configDisplayType, definition } = oligoItem;
        const parameter: Model.OligoParam = parameterMap[id];
        // only oligos whose validation did not result in an exception
        // in the service will have a value contained in the result map
        const updatedParamDefs: Model.OligoParamDefs = paramDefsMap[id] || { ...definition };
        const oligo: Model.OligoItem = {
          parameter,
          id,
          index,
          displayType,
          configDisplayType,
          definition: updatedParamDefs,
        };
        itemMap[id] = oligo;
        itemList.push(oligo);
        return acc;
      }, [[], {}] as [Model.OligoItem[], OligoItemMap]);
      const oligosWithPrices: Model.OligoItem[] = updatedOligoList.filter(isPricingCandidate);
      if (oligosWithPrices.length > 0) {
        pricingService.pricesForOligos(oligosWithPrices).then((pricingResultMap) => {
          Object.keys(pricingResultMap).forEach((id: string) => {
            const pricing: PricingResult = pricingResultMap[id];
            updatedOligoMap[id].pricing = pricing;
          });
          const updatedOligosWithOrWithoutPrices: Model.OligoItem[] = Object.keys(updatedOligoMap).map((id) => updatedOligoMap[id]);
          deferred.resolve(updatedOligosWithOrWithoutPrices);
        }, deferred.reject);
      } else {
        deferred.resolve(updatedOligoList);
      }
    }, deferred.reject);
    return deferred.promise;
  };
}

export type ProjectActionsService = ($q: ng.IQService, projectService: ProjectService, pricingService: PricingService) => ProjectActions;
export const PROJECT_ACTIONS_SERVICE_NAME = 'ProjectActionsService';
export const ProjectActionsService: ProjectActionsService = ($q: ng.IQService, projectService: ProjectService, pricingService: PricingService): ProjectActions => {
  const updateOligosUsingPreference = oligosUsingPreference($q, projectService, pricingService);
  return {
    addProject: (request: ProjectAddFnRequest) => {
      return (dispatch: ProjectAddDispatch,
              getState: () => Model.RootState,
              extraArgs: object) => {
        const deferred: ng.IDeferred<Model.ProjectItem> = $q.defer();
        const { nextIdx } = request;
        const addRequest: AddProjectRequest = { index: nextIdx, id: `${generateId()}_${nextIdx}` };
        projectService.newProjectItem(addRequest).then((project: Model.ProjectItem) => {
          dispatch({ type: ADD_PROJECT, payload: project });
          deferred.resolve(project);
        }, deferred.reject);
        return deferred.promise;
      };
    },
    updateProject: (request: ProjectUpdateFnRequest) => {
      return (dispatch: ProjectUpdateDispatch,
              getState: () => Model.RootState,
              extraArgs: object) => {
        const deferred: ng.IDeferred<Model.ProjectItem> = $q.defer();
        const { preference, index } = request;
        const { projects } = getState();
        const project: Model.ProjectItem = projects[index];
        const { oligos } = project;
        updateOligosUsingPreference(preference, oligos).then((updatedOligos: Model.OligoItem[]) => {
          updatedOligos.sort((oligo1: Model.OligoItem, oligo2: Model.OligoItem) => oligo1.index - oligo2.index);
          const payload: Model.ProjectItem = {
            ...project,
            ...{ preference },
            ...{ oligos: updatedOligos },
          };
          dispatch({ type: UPDATE_PROJECT, payload });
          deferred.resolve(payload);
        }, deferred.reject);
        return deferred.promise;
      };
    },
    deleteProject: (request: ProjectDeleteFnRequest) => {
      return (dispatch: ProjectDeleteDispatch,
              getState: () => Model.RootState,
              extraArgs: object): ng.IPromise<Model.ProjectItem> => {
        const deferred: ng.IDeferred<Model.ProjectItem> = $q.defer();
        const { index } = request;
        const project: Model.ProjectItem = getState().projects[index];
        dispatch({ type: DELETE_PROJECT, payload: request });
        deferred.resolve(project);
        return deferred.promise;
      };
    },
    importProject: (request: ProjectImportFnRequest) => {
      return (dispatch: ProjectImportDispatch,
              getState: () => Model.RootState,
              extraArgs: object): ng.IPromise<ProjectImportFnResponse> => {
        const deferred: ng.IDeferred<ProjectImportFnResponse> = $q.defer();
        let { projects } = request;
        if (projects && projects.length > 0) {
          const allOligos: Model.OligoItem[] = projects.reduce((acc: Model.OligoItem[], project: Model.ProjectItem) => {
            return [...acc, ...project.oligos];
          }, []);
          const oligosForPricing = allOligos.filter(isPricingCandidate);
          if (oligosForPricing.length > 0) {
            pricingService.pricesForOligos(oligosForPricing).then((pricingResultMap) => {
              projects = projects.map((project) => {
                const { oligos } = project;
                const updatedOligos: Model.OligoItem[] = oligos.map((oligo) => {
                  const { id } = oligo;
                  const updatedOligo: Model.OligoItem = { ...oligo };
                  const pricing = pricingResultMap[id];
                  // update the oligos whose pricing was requested
                  if (pricing) {
                    updatedOligo.pricing = pricing;
                  }
                  return updatedOligo;
                });
                return {
                  ...project,
                  oligos: updatedOligos,
                };
              });
              dispatch({ type: LOAD_PROJECTS, payload: projects });
              deferred.resolve({ projects });
            }, deferred.reject);
          } else {
            dispatch({ type: LOAD_PROJECTS, payload: projects });
            deferred.resolve({ projects });
          }
        } else {
          deferred.resolve({ projects });
        }
        return deferred.promise;
      };
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/actions/project-actions.ts